import * as Sentry from "@sentry/react";
import {
  useEffect,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "./sentry-router-deps";

export function initSentry() {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN || "https://698a9c480c98d8f8aa896d3a2ce21a56@o4510455980163073.ingest.us.sentry.io/4510456041963520",
    enabled: true,
    tracesSampleRate: 0.1,
    // Sample dashboard navigations more aggressively so we always have
    // a trace to look at when an admin/dash page errors. Other routes
    // fall back to the global tracesSampleRate above.
    tracesSampler: ({ name, attributes, parentSampled }) => {
      if (parentSampled !== undefined) return parentSampled;
      const op = attributes?.["sentry.op"];
      if (op === "pageload" || op === "navigation") {
        if (typeof name === "string" && name.startsWith("/dash")) return 1.0;
      }
      return 0.1;
    },
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
      Sentry.feedbackIntegration({
        colorScheme: "system",
        buttonLabel: "Report a Bug",
        submitButtonLabel: "Send Report",
        formTitle: "Report a Bug",
        messagePlaceholder: "What happened? What did you expect?",
        showBranding: false,
        autoInject: false,
      }),
    ],
    environment: import.meta.env.MODE,
    beforeBreadcrumb(breadcrumb) {
      // Scrub PII from network breadcrumbs
      if (breadcrumb.category === "xhr" || breadcrumb.category === "fetch") {
        delete breadcrumb.data?.request_body;
      }
      return breadcrumb;
    },
    beforeSend(event) {
      return processSentryEvent(event);
    },
  });
  sentryDsnLoaded = Boolean(
    import.meta.env.VITE_SENTRY_DSN ||
      "https://698a9c480c98d8f8aa896d3a2ce21a56@o4510455980163073.ingest.us.sentry.io/4510456041963520",
  );
}

// Exported for tests · this is the exact pipeline used by Sentry's beforeSend.
// Returns the (possibly mutated) event, or null to drop it.
export function processSentryEvent(
  event: import("@sentry/react").ErrorEvent,
): import("@sentry/react").ErrorEvent | null {
  // Strip any accidental PII from event extras/context
  if (event.extra) {
    delete event.extra["api_key"];
    delete event.extra["auth_header"];
    delete event.extra["password"];
    delete event.extra["authorization"];
  }
  if (event.request?.headers) {
    delete event.request.headers["authorization"];
    delete event.request.headers["Authorization"];
    delete event.request.headers["cookie"];
    delete event.request.headers["Cookie"];
  }
  // Never let an email address through on the user context · id only.
  if (event.user) {
    const { id } = event.user;
    event.user = id ? { id } : undefined;
  }
  // Drop noise from third-party SDKs that schedule internal timers we
  // can't cancel on unmount (HERE Maps, Cloudflare Turnstile).
  const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
  const isThirdPartyNoise = frames.some((f) => {
    const fn = f.filename ?? "";
    return (
      fn.includes("js.api.here.com") ||
      fn.includes("mapsjs-core.js") ||
      fn.includes("challenges.cloudflare.com") ||
      fn.includes("turnstile")
    );
  });
  if (isThirdPartyNoise) return null;
  recordSentryEvent({
    at: Date.now(),
    eventId: event.event_id,
    level: event.level,
    type: event.exception?.values?.[0]?.type,
    message:
      event.exception?.values?.[0]?.value ||
      (typeof event.message === "string" ? event.message : undefined),
  });
  return event;
}

// ── In-app status tracking (admin widget) ─────────────────────────────────
let sentryDsnLoaded = false;

export interface SentryLastEvent {
  at: number;
  eventId?: string;
  level?: string;
  type?: string;
  message?: string;
}

let lastEvent: SentryLastEvent | null = null;
const listeners = new Set<(snap: SentryStatusSnapshot) => void>();

export interface SentryStatusSnapshot {
  dsnLoaded: boolean;
  environment: string;
  lastEvent: SentryLastEvent | null;
}

function snapshot(): SentryStatusSnapshot {
  return {
    dsnLoaded: sentryDsnLoaded,
    environment: import.meta.env.MODE,
    lastEvent,
  };
}

function recordSentryEvent(ev: SentryLastEvent) {
  lastEvent = ev;
  const snap = snapshot();
  listeners.forEach((l) => {
    try {
      l(snap);
    } catch {
      /* listener errors must not break Sentry pipeline */
    }
  });
}

/** Subscribe to Sentry status changes. Returns an unsubscribe fn. */
export function subscribeSentryStatus(
  listener: (snap: SentryStatusSnapshot) => void,
): () => void {
  listeners.add(listener);
  listener(snapshot());
  return () => {
    listeners.delete(listener);
  };
}

/** One-shot read of current Sentry status, for SSR-safe initial state. */
export function getSentryStatus(): SentryStatusSnapshot {
  return snapshot();
}

/** Set authenticated user context on Sentry · user ID only, no email (SOC 2 MOD-2 / PII) */
export function setSentryUser(user: { id: string; email?: string } | null) {
  if (user) {
    // Do not send email to Sentry · user ID is sufficient for incident correlation
    Sentry.setUser({ id: user.id });
  } else {
    Sentry.setUser(null);
  }
}

/** Add a worker-api breadcrumb for tracing */
export function addWorkerBreadcrumb(opts: {
  method: string;
  endpoint: string;
  agentId?: string;
  companyId?: string;
  status: number;
  ok: boolean;
}) {
  Sentry.addBreadcrumb({
    category: "worker-api",
    message: `${opts.method} ${opts.endpoint}`,
    data: {
      agent_id: opts.agentId,
      company_id: opts.companyId,
      status: opts.status,
    },
    level: opts.ok ? "info" : "error",
  });
}

/** Sentry ErrorBoundary component for self-healing route recovery */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

export { Sentry };
